const DB_NAME = "eco-bain-ai-preview";
const STORE_NAME = "photos";
const ORIGINAL_PHOTO_KEY = "original-photo";
const PHOTO_READY_FLAG = "ecoBainAiPreviewPhotoReady";
const SESSION_KEY = "ecoBainAiPreviewSessionId";

function openPhotoDb() {
  return new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) {
      reject(new Error("IndexedDB is not available."));
      return;
    }

    const request = window.indexedDB.open(DB_NAME, 1);
    let timedOut = false;
    const timer = setTimeout(() => { timedOut = true; reject(new Error("Photo store timeout.")); }, 3000);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => {
      clearTimeout(timer);
      if (timedOut) request.result.close();
      else resolve(request.result);
    };
    request.onerror = () => { clearTimeout(timer); reject(request.error || new Error("Unable to open photo store.")); };
    request.onblocked = () => { clearTimeout(timer); timedOut = true; reject(new Error("Photo store blocked.")); };
  });
}

function runStoreOperation(mode, operation) {
  return openPhotoDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, mode);
        const store = transaction.objectStore(STORE_NAME);
        const request = operation(store);
        const timer = setTimeout(() => { transaction.abort(); reject(new Error("Photo store timeout.")); }, 3000);
        request.onerror = () => reject(request.error || new Error("Photo store operation failed."));
        transaction.oncomplete = () => { clearTimeout(timer); db.close(); resolve(request.result); };
        transaction.onabort = transaction.onerror = () => {
          clearTimeout(timer);
          db.close();
          reject(transaction.error || new Error("Photo store transaction failed."));
        };
      })
  );
}

export function getAiPreviewSessionId() {
  try {
    const existing = window.localStorage.getItem(SESSION_KEY);
    if (existing) return existing;

    const next =
      typeof window.crypto?.randomUUID === "function"
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    window.localStorage.setItem(SESSION_KEY, next);
    return next;
  } catch (err) {
    return "";
  }
}

export async function saveAiPreviewOriginalPhoto(file) {
  if (!file) return false;

  try {
    await runStoreOperation("readwrite", (store) => store.put(file, ORIGINAL_PHOTO_KEY));
    window.sessionStorage.setItem(PHOTO_READY_FLAG, "1");
    return true;
  } catch (err) {
    console.info("[Eco Bain AI Preview] original photo was not persisted", err);
    return false;
  }
}

export async function getAiPreviewOriginalPhoto() {
  try {
    if (window.sessionStorage.getItem(PHOTO_READY_FLAG) !== "1") return null;

    const file = await runStoreOperation("readonly", (store) => store.get(ORIGINAL_PHOTO_KEY));
    return file instanceof File ? file : null;
  } catch (err) {
    console.info("[Eco Bain AI Preview] original photo was not available", err);
    return null;
  }
}

export async function clearAiPreviewOriginalPhoto() {
  try { window.sessionStorage.removeItem(PHOTO_READY_FLAG); } catch (err) {}
  try {
    await runStoreOperation("readwrite", (store) => store.delete(ORIGINAL_PHOTO_KEY));
  } catch (err) {
    console.info("[Eco Bain AI Preview] original photo could not be cleared", err);
  }

  try {
    window.sessionStorage.removeItem(PHOTO_READY_FLAG);
  } catch (err) {}
}
